<script setup lang="ts">
import { ref } from 'vue'

const el = ref<HTMLElement | null>(null)

let isDown = false
let startX = 0
let startScrollLeft = 0

let didDrag = false
const THRESHOLD = 6 // px

function onPointerDown(e: PointerEvent) {
  const target = e.target as HTMLElement | null
  if (target?.closest('button,a,input,textarea,select,[role="button"]')) return

  if (!el.value) return
  if (e.pointerType === 'mouse' && e.button !== 0) return

  isDown = true
  didDrag = false

  startX = e.clientX
  startScrollLeft = el.value.scrollLeft

  el.value.setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
  if (!el.value || !isDown) return

  const dx = e.clientX - startX

  if (!didDrag && Math.abs(dx) > THRESHOLD) {
    didDrag = true
    // quando virou drag, melhora UX e evita seleção
    el.value.style.cursor = 'grabbing'
    el.value.style.userSelect = 'none'
  }

  if (!didDrag) return

  el.value.scrollLeft = startScrollLeft - dx
  e.preventDefault()
}

function onPointerUp(e: PointerEvent) {
  if (!el.value) return
  isDown = false

  // se arrastou, bloqueia o "click fantasma" que vem depois do pointerup
  if (didDrag) {
    e.preventDefault()
    e.stopPropagation()
  }

  el.value.style.cursor = ''
  el.value.style.userSelect = ''

  try {
    el.value.releasePointerCapture(e.pointerId)
  } catch {
    console.error('Error releasing pointer capture', e)
  }
}

function onClickCapture(e: MouseEvent) {
  // fallback extra: se arrastou, não deixa clique passar
  if (didDrag) {
    e.preventDefault()
    e.stopPropagation()
  }
  didDrag = false
}
</script>

<template>
  <div
    ref="el"
    class="drag-scroll"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @click.capture="onClickCapture"
  >
    <slot />
  </div>
</template>

<style scoped>
.drag-scroll {
  overflow-x: auto;
  overflow-y: hidden;

  /* essencial pro touch/Pointer Events não brigar com scroll */
  touch-action: pan-x;

  /* opcional, mas ajuda na “sensação mobile” */
  -webkit-overflow-scrolling: touch;

  /* opcional */
  cursor: grab;
}
</style>
